import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import axios, { API_ORIGIN } from "../axios";
import { Link, useSearchParams } from "react-router-dom";
import "../style/home.css";
import { useTranslation } from "react-i18next";
import { Search, ArrowLeft, ArrowRight, TrendingUp, Smartphone, Tablet, Laptop } from "lucide-react";
import LinkIcon from "../componant/ui/LinkIcon";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from "../componant/ui/Button";
import Logo from "../componant/ui/Logo";
import QRCode from "react-qr-code";
import { getActiveTheme, getThemeSurface } from "../utils/themes";
import { groupLinks } from "../utils/linkImage";
import ImageLinkGrid from "../componant/ImageLinkGrid";

gsap.registerPlugin(ScrollTrigger);

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const SOCIALS = [
  { icon: "fa-instagram", color: "#E1306C", width: "70%" },
  { icon: "fa-tiktok", color: "#000000", width: "50%" },
  { icon: "fa-youtube", color: "#FF0000", width: "60%" },
];

// Fake "saved links" fed through the same groupLinks()/ImageLinkGrid pipeline Setup.js and
// Show.js use for a real profile, so the customization showcase renders an actual profile
// layout (icon link rows + a photo-link tile pair) instead of an approximation of one.
const SHOWCASE_LINKS = [
  { social_name: "Instagram", icon: "fa-instagram", color: "#E1306C" },
  { social_name: "TikTok", icon: "fa-tiktok", color: "#000000" },
  { social_name: "YouTube", icon: "fa-youtube", color: "#FF0000" },
  { type: "image", social_name: "New drop" },
  { type: "image", social_name: "Latest video" },
];

// A handful of real Linkakam themes, cycled in the hero's browser-tab
// mockup to show off customization at a glance.
const TAB_THEME_NAMES = ["default", "midnight", "sunset"];
const TAB_THEMES = TAB_THEME_NAMES.map((name) => getActiveTheme(name));
const TAB_THEME_INTERVAL_MS = 3200;

const STEPS = [
  {
    eyebrow: "Step 1",
    title: "Add all your links",
    body: "Connect your socials, store, videos, music, podcast and events. It all comes together on one page designed to convert.",
  },
  {
    eyebrow: "Step 2",
    title: "Help your followers",
    body: "Help your followers discover everything you're sharing all over the internet, in one simple place. They'll thank you for it.",
  },
  {
    eyebrow: "Step 3",
    title: "Share it everywhere",
    body: "Add your unique Linkakam URL to every platform your audience is on, then use your QR code to bring offline traffic online.",
  },
];

// Angle (degrees, 0 = right, clockwise) each platform bubble flies out to
// around the QR chip in the "Share it everywhere" step mockup.
const STEP3_BUBBLE_ANGLES = [-55, 55, 180];

// Static theme surface for the "Help your followers" step's mini profile
// preview — unlike the customization showcase below, this one isn't meant
// to cycle, just to look like a real page.
const STEP2_THEME = getActiveTheme("default");
const STEP2_SURFACE = getThemeSurface(STEP2_THEME);

// Real Linkakam themes cycled in the customization showcase card.
const SHOWCASE_THEME_NAMES = ["neon", "terminal", "midnight", "sakura", "ocean"];
const SHOWCASE_THEMES = SHOWCASE_THEME_NAMES.map((name) => getActiveTheme(name));
const SHOWCASE_THEME_INTERVAL_MS = 7000;

// Device frames the customization mockup switches between, picked by the user via the
// mobile/tablet/desktop buttons. Real device-bezel images instead of CSS-drawn borders;
// each has its own screen "cutout" expressed as percentages of the frame image, same
// technique as Setup.js's PhonePreview. width/height are tween targets for the springy
// resize (computed from each image's real aspect ratio so nothing stretches).
const SHOWCASE_DEVICES = [
  {
    key: "mobile",
    label: "Mobile",
    icon: Smartphone,
    image: `${process.env.PUBLIC_URL}/images/bezels/iphone-18-pro-black.webp`,
    width: 200,
    height: 409,
    inset: { left: 5.3, top: 2.5, width: 89.3, height: 95, radius: "9% / 4.4%" },
    // How much the profile content shrinks inside the screen — a phone screen is basically
    // full of the profile, but the same profile shown on a bigger screen would look small
    // and centered, not stretched to fill it edge to edge.
    contentScale: 1,
  },
  {
    key: "tablet",
    label: "Tablet",
    icon: Tablet,
    image: `${process.env.PUBLIC_URL}/images/bezels/iP-A-11-Purple.png`,
    width: 320,
    height: 232,
    contentScale: 0.5,
    inset: { left: 4.2, top: 5.8, width: 91.6, height: 87.8, radius: "3% / 4%" },
  },
  {
    key: "desktop",
    label: "Desktop",
    icon: Laptop,
    image: `${process.env.PUBLIC_URL}/images/bezels/MB-A-13-Blue.png`,
    width: 483,
    height: 318,
    contentScale: 0.6,
    inset: { left: 11, top: 12.9, width: 78.4, height: 77.4, radius: "1.5%" },
  },
];

// Placeholder 7-day visit curve for the analytics showcase's bar chart.
const ANALYTICS_DAYS = [
  { label: "Mon", count: 42 },
  { label: "Tue", count: 68 },
  { label: "Wed", count: 55 },
  { label: "Thu", count: 90 },
  { label: "Fri", count: 62 },
  { label: "Sat", count: 98 },
  { label: "Sun", count: 121 },
];

// Placeholder click-tracking rows for the analytics showcase's mini list.
const ANALYTICS_LINKS = [
  { icon: "fa-instagram", color: "#E1306C", label: "Instagram", clicks: 1204, pct: 100 },
  { icon: "fa-tiktok", color: "#000000", label: "TikTok", clicks: 860, pct: 71 },
  { icon: "fa-youtube", color: "#FF0000", label: "Store", clicks: 640, pct: 53 },
];

// The avatar shrinks along with the rest of the profile content, but never below this
// fraction of its full size — past that it reads as a squished speck instead of a circle.
const AVATAR_MIN_SCALE = 0.7;

const QR_FG_SWATCHES = [
  "#0f1220",
  "#2a5ce6",
  "#d7277d",
  "#059669",
  "#d97706",
  "#7c3aed",
  "#0d9488",
  "#dc2626",
];
const QR_BG_SWATCHES = ["#ffffff", "#f6f7fb", "#fff7ed", "#eff6ff", "#fdf2f8", "#ecfdf5"];

const Home = () => {
  const { t } = useTranslation();

  // The query lives in the URL (?q=) so Back from a profile restores the results.
  const [params, setParams] = useSearchParams();
  const activeQuery = params.get("q");

  const [search, setSearch] = useState(activeQuery || "");
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(Boolean(activeQuery));

  const heroRef = useRef(null);
  const stackRef = useRef(null);
  const stepsRef = useRef(null);
  const visitsCountRef = useRef(null);
  const featuresRef = useRef(null);
  const analyticsChartRef = useRef(null);

  const [mockDeviceIndex, setMockDeviceIndex] = useState(0);
  const mockDevice = SHOWCASE_DEVICES[mockDeviceIndex];
  const deviceRef = useRef(null);
  const isFirstDeviceRender = useRef(true);

  const [tabThemeIndex, setTabThemeIndex] = useState(0);
  const tabTheme = TAB_THEMES[tabThemeIndex];

  const [showcaseThemeIndex, setShowcaseThemeIndex] = useState(0);
  const showcaseTheme = SHOWCASE_THEMES[showcaseThemeIndex];
  const showcaseSurface = getThemeSurface(showcaseTheme);
  // Counter-scales the avatar against the block-wide contentScale so it never renders
  // smaller than AVATAR_MIN_SCALE of its natural size (see the constant above).
  const avatarBoostScale = Math.max(1, AVATAR_MIN_SCALE / mockDevice.contentScale);

  const themeLayerRefs = useRef([]);
  const prevShowcaseIndexRef = useRef(0);
  const [wipePrevIndex, setWipePrevIndex] = useState(null);
  const shimmerRef = useRef(null);

  const pillRefs = useRef([]);
  const pillsWrapRef = useRef(null);
  const indicatorRef = useRef(null);
  const isFirstPillRender = useRef(true);
  // Gates the active pill's white text: until the indicator has actually been
  // measured and placed, the pill falls back to a solid dark bg + dark text
  // instead of white-on-nothing (which would be invisible if the measurement
  // is late — e.g. a web font swap shifting pill widths after paint).
  const [indicatorReady, setIndicatorReady] = useState(false);

  const progressBarRef = useRef(null);
  const progressTweenRef = useRef(null);
  const isShowcasePausedRef = useRef(false);

  const [qrFg, setQrFg] = useState(QR_FG_SWATCHES[0]);
  const [qrBg, setQrBg] = useState(QR_BG_SWATCHES[0]);

  const load = () => {
    const q = search.trim();
    if (q) setParams({ q });
  };

  const clearSearch = () => {
    setParams({});
    setSearch("");
    setProfiles([]);
  };

  // Selecting a theme pill: switch the real state and give the pill a
  // quick tactile bounce.
  const handleThemePillClick = (index, el) => {
    setShowcaseThemeIndex(index);
    if (!el || prefersReducedMotion()) return;
    gsap.fromTo(
      el,
      { scale: 1 },
      { scale: 1.08, duration: 0.15, yoyo: true, repeat: 1, ease: "power1.inOut" }
    );
  };

  // Pauses autoplay/progress while the pointer is anywhere in the
  // customization showcase, resumes on leave.
  const handleShowcaseMouseEnter = () => {
    isShowcasePausedRef.current = true;
    progressTweenRef.current?.pause();
  };
  const handleShowcaseMouseLeave = () => {
    isShowcasePausedRef.current = false;
    progressTweenRef.current?.resume();
  };

  useEffect(() => {
    if (!activeQuery) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`/search/${encodeURIComponent(activeQuery)}`)
      .then(({ data }) => {
        if (!cancelled) setProfiles(data.profiles);
      })
      .catch(() => {
        if (!cancelled) setProfiles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeQuery]);

  // Cycles the hero tab mockup through a few themes, forever, so the card
  // demonstrates customization without needing interaction.
  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) return;

    const id = setInterval(() => {
      setTabThemeIndex((i) => (i + 1) % TAB_THEMES.length);
    }, TAB_THEME_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Cycles the customization showcase card through a few real themes.
  // Paused (via the ref, not a re-created interval) while the pointer is
  // inside the customization section.
  useEffect(() => {
    if (prefersReducedMotion()) return;

    const id = setInterval(() => {
      if (isShowcasePausedRef.current) return;
      setShowcaseThemeIndex((i) => (i + 1) % SHOWCASE_THEMES.length);
    }, SHOWCASE_THEME_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Radial wipe + shimmer sweep whenever the showcase theme actually changes
  // (auto-advance or a manual pill click) — hover preview does not trigger this.
  useEffect(() => {
    const prev = prevShowcaseIndexRef.current;
    const current = showcaseThemeIndex;
    if (prev === current) return;

    setWipePrevIndex(prev);
    prevShowcaseIndexRef.current = current;

    const layer = themeLayerRefs.current[current];
    if (prefersReducedMotion() || !layer) return;

    gsap.fromTo(
      layer,
      { clipPath: "circle(0% at 30% 30%)" },
      { clipPath: "circle(150% at 30% 30%)", duration: 1.1, ease: "power2.inOut" }
    );

    if (shimmerRef.current) {
      gsap.fromTo(
        shimmerRef.current,
        { xPercent: -150, opacity: 0.9 },
        { xPercent: 250, opacity: 0, duration: 0.9, ease: "power1.out", delay: 0.15 }
      );
    }
  }, [showcaseThemeIndex]);

  // Slides the pill "active" indicator to sit behind whichever pill is
  // selected, measuring real DOM rects so it also works if pills wrap.
  // useLayoutEffect (not useEffect) so the very first placement happens
  // before paint — otherwise the active pill briefly shows white text with
  // no background underneath it.
  useLayoutEffect(() => {
    const positionIndicator = (animate) => {
      const activePill = pillRefs.current[showcaseThemeIndex];
      const indicator = indicatorRef.current;
      const wrap = pillsWrapRef.current;
      if (!activePill || !indicator || !wrap) return;

      const wrapRect = wrap.getBoundingClientRect();
      const pillRect = activePill.getBoundingClientRect();
      const target = {
        x: pillRect.left - wrapRect.left,
        y: pillRect.top - wrapRect.top,
        width: pillRect.width,
        height: pillRect.height,
      };

      if (!animate || prefersReducedMotion() || isFirstPillRender.current) {
        gsap.set(indicator, target);
        isFirstPillRender.current = false;
      } else {
        gsap.to(indicator, { ...target, duration: 0.45, ease: "power3.out" });
      }
      setIndicatorReady(true);
    };

    positionIndicator(true);

    // Web-font swaps and window resizes can shift pill widths after the
    // initial measurement; snap (don't animate) the indicator back in place.
    const resnap = () => positionIndicator(false);
    window.addEventListener("resize", resnap);
    document.fonts?.ready?.then(resnap);
    return () => window.removeEventListener("resize", resnap);
  }, [showcaseThemeIndex]);

  // Progress bar under the pills fills over one autoplay interval and
  // restarts on every theme change (manual or automatic).
  useEffect(() => {
    const el = progressBarRef.current;
    if (!el) return;

    progressTweenRef.current?.kill();
    if (prefersReducedMotion()) {
      gsap.set(el, { scaleX: 1 });
      return;
    }
    gsap.set(el, { scaleX: 0 });
    progressTweenRef.current = gsap.to(el, {
      scaleX: 1,
      duration: SHOWCASE_THEME_INTERVAL_MS / 1000,
      ease: "none",
      paused: isShowcasePausedRef.current,
    });
    return () => progressTweenRef.current?.kill();
  }, [showcaseThemeIndex]);

  // Morphs the device frame's width/height with a springy ease instead of a flat
  // CSS transition, so picking a different device via the buttons feels like a real resize.
  useLayoutEffect(() => {
    const el = deviceRef.current;
    if (!el) return;
    const target = { width: mockDevice.width, height: mockDevice.height };
    if (prefersReducedMotion() || isFirstDeviceRender.current) {
      gsap.set(el, target);
      isFirstDeviceRender.current = false;
      return;
    }
    gsap.to(el, { ...target, duration: 0.9, ease: "elastic.out(1, 0.75)" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockDeviceIndex]);

  // Placeholder proof-point figure: counts up once on mount instead of
  // showing a static hard-coded number.
  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const el = visitsCountRef.current;
    if (!el) return;

    if (reduceMotion) {
      el.textContent = "2,481";
      return;
    }

    const counter = { value: 0 };
    const tween = gsap.to(counter, {
      value: 2481,
      duration: 1.8,
      delay: 0.6,
      ease: "power2.out",
      onUpdate: () => {
        el.textContent = Math.round(counter.value).toLocaleString();
      },
    });
    return () => tween.kill();
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const ctx = gsap.context(() => {
      if (reduceMotion) {
        gsap.set(
          ["[data-hero-eyebrow]", "[data-hero-headline]", "[data-hero-sub]", "[data-hero-actions]", "[data-hero-card]"],
          { opacity: 1, y: 0, scale: 1 }
        );
        return;
      }

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from("[data-hero-eyebrow]", { opacity: 0, y: 14, duration: 0.5 })
        .from("[data-hero-headline]", { opacity: 0, y: 22, duration: 0.7 }, "-=0.25")
        .from("[data-hero-sub]", { opacity: 0, y: 16, duration: 0.6 }, "-=0.35")
        .from("[data-hero-actions]", { opacity: 0, y: 12, duration: 0.5 }, "-=0.3")
        .from(
          "[data-hero-card]",
          { opacity: 0, y: 30, scale: 0.96, stagger: 0.12, duration: 0.7 },
          "-=0.5"
        );

      // Idle drift on the card stack, tied to pointer position within the hero.
      const hero = heroRef.current;
      const stack = stackRef.current;
      if (hero && stack) {
        // The phone stays put; only the floating chips drift.
        const cards = stack.querySelectorAll("[data-hero-card]:not([data-hero-anchor])");
        const onMove = (e) => {
          const rect = hero.getBoundingClientRect();
          const px = (e.clientX - rect.left) / rect.width - 0.5;
          const py = (e.clientY - rect.top) / rect.height - 0.5;
          cards.forEach((card, i) => {
            const depth = (i + 1) * 6;
            gsap.to(card, {
              x: px * depth,
              y: py * depth,
              duration: 0.8,
              ease: "power2.out",
              overwrite: "auto",
            });
          });
        };
        hero.addEventListener("mousemove", onMove);
        return () => hero.removeEventListener("mousemove", onMove);
      }
    }, heroRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion || !stepsRef.current) return;

    const ctx = gsap.context(() => {
      const items = gsap.utils.toArray("[data-step]");
      items.forEach((item, i) => {
        const dot = item.querySelector("[data-step-dot]");
        const mock = item.querySelector("[data-step-mock]");
        const fromSide = item.getAttribute("data-step-side") === "right" ? 30 : -30;
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            start: "top 82%",
          },
        });
        tl.from(item.querySelector("[data-step-text]"), {
          opacity: 0,
          y: 20,
          duration: 0.6,
          ease: "power2.out",
        });
        if (dot) {
          tl.from(
            dot,
            { scale: 0, opacity: 0, duration: 0.4, ease: "back.out(2.6)" },
            "<"
          );
        }
        if (mock) {
          tl.from(
            mock,
            { opacity: 0, x: fromSide, scale: 0.96, duration: 0.65, ease: "power3.out" },
            "<0.05"
          );
        }
      });

      // Vertical connector threading the three steps together, drawn in as the
      // section scrolls into view (a spine, not just per-step decoration).
      const line = document.querySelector("[data-steps-connector]");
      if (line) {
        gsap.fromTo(
          line,
          { scaleY: 0 },
          {
            scaleY: 1,
            ease: "none",
            transformOrigin: "top center",
            scrollTrigger: {
              trigger: stepsRef.current,
              start: "top 70%",
              end: "bottom 75%",
              scrub: 0.6,
            },
          }
        );
      }

      // Step 1 mockup: link rows stagger in like they're being added live.
      const step1Rows = gsap.utils.toArray("[data-step1-row]");
      if (step1Rows.length) {
        gsap.from(step1Rows, {
          opacity: 0,
          y: 12,
          duration: 0.4,
          stagger: 0.12,
          ease: "power2.out",
          scrollTrigger: { trigger: step1Rows[0], start: "top 85%" },
        });
      }

      // Step 2 mockup: a cursor ring lands and pulses on a link, implying a
      // visitor browsing the profile.
      const cursor = document.querySelector("[data-step2-cursor]");
      if (cursor) {
        const tl = gsap.timeline({
          scrollTrigger: { trigger: cursor, start: "top 85%" },
        });
        tl.from(cursor, { opacity: 0, scale: 0.4, duration: 0.35, ease: "power2.out" });
        tl.to(cursor, {
          scale: 1.6,
          opacity: 0,
          duration: 1,
          ease: "power1.out",
          repeat: -1,
          repeatDelay: 0.6,
        });
      }

      // Step 3 mockup: broadcast rings pulse outward from the QR chip, and
      // platform bubbles fly out around it to represent sharing everywhere.
      const rings = gsap.utils.toArray("[data-step3-ring]");
      if (rings.length) {
        const tl = gsap.timeline({
          scrollTrigger: { trigger: rings[0], start: "top 85%" },
        });
        tl.fromTo(
          rings,
          { scale: 0.4, opacity: 0.7 },
          {
            scale: 1.6,
            opacity: 0,
            duration: 1.6,
            ease: "power1.out",
            stagger: 0.5,
            repeat: -1,
            repeatDelay: 0.4,
          }
        );

        const bubbles = gsap.utils.toArray("[data-step3-bubble]");
        gsap.fromTo(
          bubbles,
          { opacity: 0, scale: 0.5, x: 0, y: 0 },
          {
            opacity: 1,
            scale: 1,
            x: (i) => Math.cos((STEP3_BUBBLE_ANGLES[i] * Math.PI) / 180) * 72,
            y: (i) => Math.sin((STEP3_BUBBLE_ANGLES[i] * Math.PI) / 180) * 72,
            duration: 0.6,
            stagger: 0.1,
            ease: "back.out(1.8)",
            scrollTrigger: { trigger: rings[0], start: "top 85%" },
          }
        );
      }
    }, stepsRef);

    return () => ctx.revert();
  }, []);

  // Scroll-reveal for the feature showcase sections (customization, analytics, QR),
  // each sliding in from the side its mockup sits on.
  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion || !featuresRef.current) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray("[data-feature]").forEach((el) => {
        const fromSide = el.getAttribute("data-feature") === "right" ? 40 : -40;
        gsap.from(el, {
          opacity: 0,
          x: fromSide,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
          },
        });
      });

      gsap.utils.toArray("[data-feature-pop]").forEach((el, i) => {
        gsap.from(el, {
          opacity: 0,
          y: 16,
          scale: 0.94,
          duration: 0.5,
          delay: i * 0.06,
          ease: "back.out(1.8)",
          scrollTrigger: {
            trigger: el,
            start: "top 88%",
          },
        });
      });

      // Analytics bars grow in from the baseline once the chart scrolls into view.
      const bars = gsap.utils.toArray("[data-analytics-bar]");
      if (bars.length) {
        bars.forEach((bar, i) => {
          const targetHeight = Number(bar.getAttribute("data-height"));
          const targetY = Number(bar.getAttribute("data-y"));
          gsap.fromTo(
            bar,
            { attr: { height: 0, y: targetY + targetHeight } },
            {
              attr: { height: targetHeight, y: targetY },
              duration: 0.7,
              delay: i * 0.06,
              ease: "power2.out",
              scrollTrigger: {
                trigger: analyticsChartRef.current,
                start: "top 80%",
              },
            }
          );
        });
      }
    }, featuresRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      {!activeQuery ? (
        <>
          <section
            ref={heroRef}
            className="relative overflow-hidden bg-white"
          >
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-accent-50 blur-3xl"
            />
            <div className="relative max-w-8xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 grid md:grid-cols-2 gap-16 items-center">
              <div>
                {/* <p
                  data-hero-eyebrow
                  className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide uppercase text-accent-600 bg-accent-50 rounded-full px-3 py-1"
                >
                  {t("Free. No paywall on the core tools.")}
                </p> */}
                <h1
                  data-hero-headline
                  className="mt-5 font-display font-extrabold leading-[1.05] text-4xl md:text-5xl text-ink-900"
                >
                  {t("Everything you make,")}
                  <br />
                  {t("one link away.")}
                </h1>
                <p
                  data-hero-sub
                  className="mt-6 text-base md:text-lg text-ink-500 max-w-md"
                >
                  {t(
                    "Create a single, shareable link for all your social profiles, business pages, product catalogs and menus — with built-in forms, analytics and QR codes, free."
                  )}
                </p>

                <div data-hero-actions className="mt-9 flex flex-wrap items-center gap-4">
                  <Button to="/en/setup" variant="primary" size="lg">
                    {t("Create your link")}
                  </Button>
                  <a
                    href="#how-it-works"
                    className="text-sm font-semibold text-ink-600 hover:text-brand-600 underline underline-offset-4"
                  >
                    {t("See how it works")} &rarr;
                  </a>
                </div>

                <div data-hero-actions className="mt-10 max-w-sm">
                  <label htmlFor="hero-search" className="sr-only">
                    {t("Search For Profile")}
                  </label>
                  <div className="flex items-center gap-2 rounded-full border border-ink-200 bg-white pl-4 pr-1.5 h-12 focus-within:border-brand-500 transition-colors">
                    <Search className="w-5 h-5 text-ink-400 shrink-0" aria-hidden="true" />
                    <input
                      id="hero-search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && load()}
                      placeholder={t("Search For Profile")}
                      type="text"
                      className="w-full text-sm text-ink-800 placeholder-ink-400 bg-transparent focus:outline-none"
                    />
                    <button
                      onClick={load}
                      className="h-9 px-4 rounded-full text-sm font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 transition-colors shrink-0"
                    >
                      {t("Search")}
                    </button>
                  </div>
                </div>
              </div>

              {/* Signature element: a real Linkakam profile card — its own social
                  links, its own address bar — with proof-point chips scattered
                  around it. Not a phone mockup, not a symmetric hub. */}
              <div
                ref={stackRef}
                className="relative h-[440px] md:h-[520px] select-none"
              >
                {/* Anchor: the product mockup. Centred with flexbox, not transforms, because
                    GSAP owns the transform and would overwrite -translate-x-1/2 (the phone drifted right). */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {/* Soft contact shadow on the "ground" under the phone */}
                  <div
                    aria-hidden="true"
                    className="absolute bottom-[3%] left-1/2 h-8 w-44 -translate-x-1/2 rounded-full bg-brand-900/30 blur-xl"
                  />
                  <img
                    data-hero-card
                    data-hero-anchor
                    src={`${process.env.PUBLIC_URL}/images/Iphone-17-linkakam-page.webp`}
                    alt={t("A Linkakam profile page on a phone")}
                    className="relative h-[92%] w-auto max-w-none
                      [filter:drop-shadow(0_36px_44px_rgba(88,28,135,0.38))_drop-shadow(0_14px_18px_rgba(15,23,42,0.28))_drop-shadow(0_2px_4px_rgba(15,23,42,0.25))]"
                  />
                </div>

                {/* Compact browser tab, overlapping the phone's left edge.
                    Cycles through a few real themes to show off customization. */}
                <div
                  data-hero-card
                  aria-hidden="true"
                  className="absolute left-0 top-10 w-40 rotate-[-3deg]
                    rounded-xl shadow-elevated border border-ink-100 overflow-hidden"
                >
                  <div className="flex items-center gap-1 px-2.5 py-1.5 border-b border-ink-100 bg-ink-50">
                    <span className="w-1.5 h-1.5 rounded-full bg-ink-200" />
                    <span className="w-1.5 h-1.5 rounded-full bg-ink-200" />
                    <span className="w-1.5 h-1.5 rounded-full bg-ink-200" />
                    <span className="ml-1.5 text-[9px] text-ink-400 truncate">
                      linkakam.com/yourname
                    </span>
                  </div>
                  <div className="relative p-3 overflow-hidden">
                    {/* Stacked gradient layers, crossfaded via opacity — a
                        CSS "background" transition cannot interpolate
                        between two gradients, it just snaps. */}
                    <div className="absolute inset-0" aria-hidden="true">
                      {TAB_THEMES.map((theme, i) => (
                        <div
                          key={theme.name}
                          className="absolute inset-0"
                          style={{
                            background: theme.gradient,
                            opacity: i === tabThemeIndex ? 1 : 0,
                            transition: "opacity 1.2s ease-in-out",
                          }}
                        />
                      ))}
                    </div>
                    <div className="relative">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-500 to-accent-500" />
                      <p
                        className="font-display font-bold text-[11px]"
                        style={{ color: tabTheme.text, transition: "color 1.2s ease-in-out" }}
                      >
                        Linkakam
                      </p>
                    </div>
                    <div className="mt-2.5 space-y-1.5">
                      {SOCIALS.map((s, i) => (
                        <React.Fragment key={s.icon}>
                          {i === SOCIALS.length - 1 && (
                            <div className="flex gap-1.5">
                              {[0, 1].map((j) => (
                                <div
                                  key={j}
                                  className="flex-1 aspect-square rounded-lg"
                                  style={{
                                    background: tabTheme.isDark
                                      ? "rgba(255, 255, 255, 0.14)"
                                      : "rgba(255, 255, 255, 0.7)",
                                    border: `1px solid ${
                                      tabTheme.isDark
                                        ? "rgba(255, 255, 255, 0.22)"
                                        : "rgba(15, 23, 42, 0.08)"
                                    }`,
                                    transition:
                                      "background 1.2s ease-in-out, border-color 1.2s ease-in-out",
                                  }}
                                />
                              ))}
                            </div>
                          )}
                          <div
                            className="flex items-center gap-2 rounded-lg px-2 py-1.5"
                            style={{
                              background: tabTheme.isDark
                                ? "rgba(255, 255, 255, 0.14)"
                                : "rgba(255, 255, 255, 0.7)",
                              border: `1px solid ${
                                tabTheme.isDark ? "rgba(255, 255, 255, 0.22)" : "rgba(15, 23, 42, 0.08)"
                              }`,
                              transition: "background 1.2s ease-in-out, border-color 1.2s ease-in-out",
                            }}
                          >
                            <LinkIcon value={s.icon} color={s.color} className="text-[11px] w-3 shrink-0" />
                            <span
                              className="h-1.5 rounded-full flex-1"
                              style={{
                                maxWidth: s.width,
                                background: tabTheme.isDark
                                  ? "rgba(255, 255, 255, 0.35)"
                                  : "rgba(15, 23, 42, 0.15)",
                                transition: "background 1.2s ease-in-out",
                              }}
                            />
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                    </div>
                  </div>
                </div>

                {/* Visits proof-point, offset high and to the right */}
                <div
                  data-hero-card
                  className="absolute right-2 top-0 w-36 rotate-[5deg]
                    rounded-2xl bg-ink-900 text-white shadow-elevated p-4"
                >
                  <p className="text-[10px] uppercase tracking-wide text-ink-300">
                    {t("Profile visits")}
                  </p>
                  <p className="mt-1 font-display text-2xl font-extrabold">
                    <span ref={visitsCountRef}>0</span>
                  </p>
                  <div className="mt-3 flex items-end gap-1 h-8">
                    {[40, 65, 50, 80, 60, 95, 70].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-sm bg-accent-500/80"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>

                {/* QR chip, tucked low and off to the left of the card */}
                <div
                  data-hero-card
                  className="absolute left-0 bottom-8 w-36 rotate-[4deg]
                    rounded-2xl bg-white shadow-elevated border border-ink-100 p-3 flex items-center gap-2.5"
                >
                  <a
                    href="https://linkakam.com"
                    aria-label="linkakam.com"
                    className="shrink-0 block rounded-lg bg-white"
                  >
                    <QRCode value="https://linkakam.com" size={44} level="M" />
                  </a>
                  <span className="text-[11px] font-medium text-ink-500 leading-tight">
                    {t("Scan to share")}
                  </span>
                </div>

                {/* Small brand tag, bridging the QR chip and the card */}
                <div
                  data-hero-card
                  className="absolute left-16 bottom-0 rotate-[-2deg]
                    rounded-full bg-brand-50 border border-brand-100 px-3 py-1.5
                    text-[11px] font-semibold text-brand-700"
                >
                  linkakam.com
                </div>
              </div>
            </div>
          </section>

          <section
            id="how-it-works"
            ref={stepsRef}
            className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 scroll-mt-24 overflow-hidden"
          >
            <p className="text-center text-sm font-semibold uppercase tracking-wide text-brand-600">
              {t("How it works")}
            </p>
            <h2 className="mt-3 text-center font-display font-extrabold text-3xl md:text-4xl text-ink-900">
              {t("From links to one page, in minutes.")}
            </h2>

            <div className="relative mt-20 md:mt-24 space-y-20 md:space-y-28">
              {/* Spine threading the three steps together */}
              <div
                data-steps-connector
                aria-hidden="true"
                className="hidden md:block absolute top-2 bottom-2 left-8 w-0.5 bg-gradient-to-b from-brand-300 via-brand-500 to-accent-400 origin-top"
              />

              {/* Step 1 — Add all your links */}
              <div data-step data-step-side="left" className="relative pl-12 md:pl-24 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <span
                  data-step-dot
                  aria-hidden="true"
                  className="hidden md:flex absolute left-8 top-1.5 -translate-x-1/2 w-4 h-4 rounded-full bg-brand-500 ring-4 ring-white z-10"
                />
                <div data-step-text className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute -top-8 -left-1 font-display font-extrabold text-7xl md:text-8xl leading-none text-brand-50 select-none -z-10"
                  >
                    01
                  </span>
                  <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
                    {t(STEPS[0].eyebrow)}
                  </p>
                  <p className="mt-2 font-display font-bold text-2xl text-ink-900">
                    {t(STEPS[0].title)}
                  </p>
                  <p className="mt-3 text-sm text-ink-500 leading-relaxed max-w-sm">
                    {t(STEPS[0].body)}
                  </p>
                </div>

                <div data-step-mock className="relative">
                  <div className="rounded-[1.75rem] bg-white border border-ink-100 shadow-elevated p-5 max-w-xs mx-auto md:mx-0">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-semibold text-ink-500">{t("Your links")}</p>
                      <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-sm font-bold leading-none">
                        +
                      </span>
                    </div>
                    <div className="space-y-2">
                      {SOCIALS.map((s) => (
                        <div
                          key={s.icon}
                          data-step1-row
                          className="flex items-center gap-2.5 rounded-xl border border-ink-100 px-3 py-2.5"
                        >
                          <LinkIcon value={s.icon} color={s.color} className="text-sm w-4 shrink-0" />
                          <span
                            className="h-1.5 rounded-full flex-1 bg-ink-100"
                            style={{ maxWidth: s.width }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 — Help your followers */}
              <div data-step data-step-side="right" className="relative pl-12 md:pl-24 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <span
                  data-step-dot
                  aria-hidden="true"
                  className="hidden md:flex absolute left-8 top-1.5 -translate-x-1/2 w-4 h-4 rounded-full bg-brand-500 ring-4 ring-white z-10"
                />
                <div data-step-mock className="relative md:order-2">
                  <div className="relative rounded-[1.75rem] bg-white border border-ink-100 shadow-elevated p-6 max-w-xs mx-auto md:mx-0 md:ml-auto">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-full bg-ink-100 flex items-center justify-center overflow-hidden shrink-0">
                        <img
                          src={`${process.env.PUBLIC_URL}/images/logo-only.svg`}
                          alt=""
                          className="w-6 h-6 opacity-70 object-contain"
                        />
                      </div>
                      <p className="mt-2 font-display font-bold text-xs text-ink-900">@yourname</p>
                      <div className="mt-4 w-full space-y-2">
                        {groupLinks(SHOWCASE_LINKS).map((block) =>
                          block.kind === "images" ? (
                            <ImageLinkGrid
                              key={"how-images-" + block.entries[0].index}
                              items={block.entries}
                              themeSurface={STEP2_SURFACE}
                              preview
                            />
                          ) : (
                            <div
                              key={block.index}
                              className="flex items-center justify-between gap-2 rounded-xl border border-ink-100 px-3 py-2"
                            >
                              <span className="text-[11px] font-medium text-ink-700 truncate">
                                {block.item.social_name}
                              </span>
                              <span className="w-6 h-6 rounded-full bg-ink-50 flex items-center justify-center shrink-0">
                                <LinkIcon value={block.item.icon} color={block.item.color} className="text-xs" />
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                    <span
                      data-step2-cursor
                      aria-hidden="true"
                      className="absolute w-7 h-7 rounded-full border-2 border-brand-500 pointer-events-none"
                      style={{ top: "58%", left: "78%" }}
                    />
                  </div>
                </div>

                <div data-step-text className="relative md:order-1 md:text-right">
                  <span
                    aria-hidden="true"
                    className="absolute -top-8 left-0 md:left-auto md:right-0 font-display font-extrabold text-7xl md:text-8xl leading-none text-brand-50 select-none -z-10"
                  >
                    02
                  </span>
                  <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
                    {t(STEPS[1].eyebrow)}
                  </p>
                  <p className="mt-2 font-display font-bold text-2xl text-ink-900">
                    {t(STEPS[1].title)}
                  </p>
                  <p className="mt-3 text-sm text-ink-500 leading-relaxed max-w-sm md:ml-auto">
                    {t(STEPS[1].body)}
                  </p>
                </div>
              </div>

              {/* Step 3 — Share it everywhere */}
              <div data-step data-step-side="left" className="relative pl-12 md:pl-24 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <span
                  data-step-dot
                  aria-hidden="true"
                  className="hidden md:flex absolute left-8 top-1.5 -translate-x-1/2 w-4 h-4 rounded-full bg-brand-500 ring-4 ring-white z-10"
                />
                <div data-step-text className="relative">
                  <span
                    aria-hidden="true"
                    className="absolute -top-8 -left-1 font-display font-extrabold text-7xl md:text-8xl leading-none text-brand-50 select-none -z-10"
                  >
                    03
                  </span>
                  <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
                    {t(STEPS[2].eyebrow)}
                  </p>
                  <p className="mt-2 font-display font-bold text-2xl text-ink-900">
                    {t(STEPS[2].title)}
                  </p>
                  <p className="mt-3 text-sm text-ink-500 leading-relaxed max-w-sm">
                    {t(STEPS[2].body)}
                  </p>
                </div>

                <div data-step-mock className="relative flex items-center justify-center py-4">
                  {[0, 1].map((i) => (
                    <span
                      key={i}
                      data-step3-ring
                      aria-hidden="true"
                      className="absolute w-32 h-32 rounded-full border border-brand-200 pointer-events-none"
                    />
                  ))}
                  <div className="relative z-10 rounded-2xl bg-white shadow-elevated border border-ink-100 p-4 flex flex-col items-center gap-2">
                    <QRCode value="https://linkakam.com/yourname" size={64} level="M" />
                    <span className="text-[10px] font-medium text-ink-400">linkakam.com/yourname</span>
                  </div>
                  {SOCIALS.map((s, i) => (
                    <span
                      key={s.icon}
                      data-step3-bubble
                      aria-hidden="true"
                      className="absolute w-9 h-9 rounded-full bg-white shadow-elevated border border-ink-100 flex items-center justify-center z-20"
                    >
                      <LinkIcon value={s.icon} color={s.color} className="text-xs w-3.5" />
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <div ref={featuresRef}>
            {/* Feature: total customization */}
            <section
              className="max-w-8xl mx-auto px-6 md:px-10 py-20 md:py-28 overflow-hidden"
              onMouseEnter={handleShowcaseMouseEnter}
              onMouseLeave={handleShowcaseMouseLeave}
            >
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <div data-feature="left">
                  <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
                    {t("Customization")}
                  </p>
                  <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-ink-900">
                    {t("Design that fits your brand.")}
                  </h2>
                  <p className="mt-5 text-base text-ink-500 max-w-md leading-relaxed">
                    {t(
                      "Choose from a massive library of built-in themes like Neon, Terminal, Midnight, Sakura and Ocean. Customize your avatar, update your bio, set up glassmorphism link cards and upload a personalized cover photo. Everything updates live as you type."
                    )}
                  </p>
                  <div ref={pillsWrapRef} className="relative mt-7 flex flex-wrap gap-2">
                    <div
                      ref={indicatorRef}
                      aria-hidden="true"
                      className="absolute top-0 left-0 rounded-full bg-ink-900 z-0"
                    />
                    {SHOWCASE_THEME_NAMES.map((name, i) => (
                      <button
                        key={name}
                        ref={(el) => (pillRefs.current[i] = el)}
                        type="button"
                        onClick={(e) => handleThemePillClick(i, e.currentTarget)}
                        aria-pressed={i === showcaseThemeIndex}
                        className={`relative z-10 px-4 py-2 rounded-full text-xs font-semibold capitalize border transition-colors ${
                          i === showcaseThemeIndex
                            ? indicatorReady
                              ? "text-white border-transparent"
                              : "bg-ink-900 text-white border-ink-900"
                            : "text-ink-600 border-ink-200 hover:border-brand-300"
                        }`}
                      >
                        {t(name)}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 h-1 w-full max-w-[280px] rounded-full bg-ink-100 overflow-hidden">
                    <div
                      ref={progressBarRef}
                      className="h-full w-full origin-left rounded-full bg-brand-500"
                      style={{ transform: "scaleX(0)" }}
                    />
                  </div>
                </div>

                <div data-feature="right" className="flex flex-col items-center gap-6">
                  <div className="flex items-center gap-2">
                    {SHOWCASE_DEVICES.map((device, i) => {
                      const Icon = device.icon;
                      return (
                        <button
                          key={device.key}
                          type="button"
                          onClick={() => setMockDeviceIndex(i)}
                          aria-pressed={i === mockDeviceIndex}
                          aria-label={t(device.label)}
                          title={t(device.label)}
                          className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
                            i === mockDeviceIndex
                              ? "bg-ink-900 text-white border-ink-900"
                              : "text-ink-500 border-ink-200 hover:border-brand-300 hover:text-ink-700"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                        </button>
                      );
                    })}
                  </div>
                  <div className="relative flex justify-center">
                    <div
                      aria-hidden="true"
                      className="absolute -inset-8 rounded-full bg-brand-50 blur-3xl -z-10"
                    />
                    <div
                      ref={deviceRef}
                      className="relative mx-auto"
                      style={{
                        // Only the FIRST paint's size comes from state — GSAP owns every
                        // update after that. Wiring `mockDevice.width/height` in here directly
                        // would make React re-snap it on every render (including unrelated
                        // ones, like a pill hover), fighting the GSAP tween to a standstill.
                        width: SHOWCASE_DEVICES[0].width,
                        height: SHOWCASE_DEVICES[0].height,
                        maxWidth: "100%",
                      }}
                    >
                      <div
                        className="absolute overflow-hidden shadow-elevated"
                        style={{
                          left: `${mockDevice.inset.left}%`,
                          top: `${mockDevice.inset.top}%`,
                          width: `${mockDevice.inset.width}%`,
                          height: `${mockDevice.inset.height}%`,
                          borderRadius: mockDevice.inset.radius,
                        }}
                      >
                        <div className="absolute inset-0" aria-hidden="true">
                          {SHOWCASE_THEMES.map((theme, i) => (
                            <div
                              key={theme.name}
                              ref={(el) => (themeLayerRefs.current[i] = el)}
                              className="absolute inset-0"
                              style={{
                                background: theme.gradient,
                                opacity: i === showcaseThemeIndex || i === wipePrevIndex ? 1 : 0,
                                zIndex: i === showcaseThemeIndex ? 2 : i === wipePrevIndex ? 1 : 0,
                              }}
                            />
                          ))}
                          {/* Light sweep triggered right after each real theme change */}
                          <div
                            ref={shimmerRef}
                            className="absolute inset-y-0 w-1/3 -skew-x-12 opacity-0 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
                              zIndex: 4,
                            }}
                          />
                        </div>
                        <div
                          className="preview-no-scrollbar relative p-6 pt-9 flex flex-col items-center transition-transform duration-700 ease-out"
                          style={{
                            zIndex: 10,
                            // No h-full/overflow here: clipping has to happen on the SCALED
                            // (rendered) size via the screen-clip ancestor's overflow-hidden,
                            // not on this box's own pre-scale layout height — clipping first
                            // and scaling the leftovers would cut real content off before it
                            // ever got the chance to shrink to fit.
                            transform: `scale(${mockDevice.contentScale})`,
                            transformOrigin: "top center",
                          }}
                        >
                          {/* Profile picture placeholder — the brand mark, same avatar-circle
                              treatment (border + shadow) Setup.js/Show.js use for a real photo */}
                          <div
                            className="w-16 h-16 shrink-0 rounded-full shadow-md flex items-center justify-center overflow-hidden transition-all duration-700 ease-out"
                            style={{
                              aspectRatio: "1 / 1",
                              transform: `scale(${avatarBoostScale})`,
                              background: showcaseTheme.isDark
                                ? "rgba(255, 255, 255, 0.16)"
                                : "rgba(15, 23, 42, 0.06)",
                              border: `4px solid ${showcaseTheme.isDark ? "rgba(255, 255, 255, 0.85)" : "#ffffff"
                                }`,
                            }}
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}/images/logo-only.svg`}
                              alt=""
                              className="w-9 h-9 object-contain"
                              style={{ opacity: showcaseTheme.isDark ? 0.9 : 0.75 }}
                            />
                          </div>
                          {/* Title placeholder */}
                          <p
                            className="mt-3 font-display font-bold text-sm transition-colors duration-500"
                            style={{ color: showcaseTheme.text }}
                          >
                            @yourname
                          </p>
                          {/* Bio placeholder */}
                          <div className="mt-1.5 flex flex-col items-center gap-1">
                            {["92%", "62%"].map((w) => (
                              <span
                                key={w}
                                className="h-1.5 rounded-full transition-colors duration-500"
                                style={{
                                  width: w,
                                  background: showcaseTheme.isDark
                                    ? "rgba(255, 255, 255, 0.4)"
                                    : showcaseTheme.text,
                                  opacity: showcaseTheme.isDark ? 0.7 : 0.35,
                                }}
                              />
                            ))}
                          </div>
                          {/* Links — the exact groupLinks()/ImageLinkGrid pipeline Setup.js and
                              Show.js render a real profile with, so link cards pick up each
                              theme's real shape (radius/border/shadow), not an approximation. */}
                          <div className="mt-5 w-full space-y-2.5">
                            {groupLinks(SHOWCASE_LINKS).map((block) =>
                              block.kind === "images" ? (
                                <ImageLinkGrid
                                  key={"images-" + block.entries[0].index}
                                  items={block.entries}
                                  themeSurface={showcaseSurface}
                                  preview
                                />
                              ) : (
                                <div
                                  key={block.index}
                                  className="flex items-center justify-between gap-2 px-3 py-2 transition-all duration-500"
                                  style={showcaseSurface.card}
                                >
                                  <span
                                    className="text-[11px] font-medium truncate transition-colors duration-500"
                                    style={{ color: showcaseTheme.text }}
                                  >
                                    {block.item.social_name}
                                  </span>
                                  <span
                                    className="w-6 h-6 flex items-center justify-center shrink-0 transition-all duration-500"
                                    style={showcaseSurface.iconWrap}
                                  >
                                    <LinkIcon
                                      value={block.item.icon}
                                      color={showcaseSurface.uniformIcons ? showcaseSurface.iconColor : block.item.color}
                                      onBackground={showcaseSurface.iconWrap.background}
                                      className="text-xs"
                                    />
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                      <img
                        src={mockDevice.image}
                        alt={t("Device preview frame")}
                        className="absolute inset-0 w-full h-full pointer-events-none select-none"
                        style={{ zIndex: 5 }}
                        draggable={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Feature: advanced analytics */}
            <section className="bg-ink-50/60 py-20 md:py-28 overflow-hidden">
              <div className="max-w-8xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
                <div data-feature="left" className="md:order-2">
                  <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
                    {t("Analytics")}
                  </p>
                  <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-ink-900">
                    {t("Know exactly how your page is performing.")}
                  </h2>
                  <p className="mt-5 text-base text-ink-500 max-w-md leading-relaxed">
                    {t(
                      "Get real-time insights without needing a premium upgrade. Track your total visits, monthly averages and day-by-day traffic graphs over the last 30 days. See exactly which content is driving engagement with click-tracking for every individual link."
                    )}
                  </p>
                  <div className="mt-7 grid grid-cols-3 gap-3 max-w-md">
                    {[
                      { label: "Total visits", value: "12,480" },
                      { label: "Monthly avg.", value: "2,481" },
                      { label: "Today", value: "186" },
                    ].map((stat) => (
                      <div
                        key={stat.label}
                        data-feature-pop
                        className="rounded-2xl bg-white border border-ink-100 px-3 py-3 text-center shadow-soft"
                      >
                        <p className="font-display font-extrabold text-lg text-ink-900">
                          {stat.value}
                        </p>
                        <p className="mt-0.5 text-[11px] text-ink-500 leading-tight">
                          {t(stat.label)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div data-feature="right" className="md:order-1 space-y-4">
                  <div
                    ref={analyticsChartRef}
                    data-feature-pop
                    className="rounded-[1.75rem] bg-white border border-ink-100 shadow-elevated p-5"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-ink-500">
                        {t("Visits, last 7 days")}
                      </p>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                        <TrendingUp className="w-3.5 h-3.5" aria-hidden="true" />
                        +18%
                      </span>
                    </div>
                    <svg viewBox="0 0 280 140" className="mt-3 w-full h-auto" role="img" aria-label={t("Visits, last 7 days")}>
                      {ANALYTICS_DAYS.map((d, i) => {
                        const max = Math.max(...ANALYTICS_DAYS.map((x) => x.count));
                        const slot = 280 / ANALYTICS_DAYS.length;
                        const barW = slot * 0.5;
                        const h = (d.count / max) * 96;
                        const x = slot * i + (slot - barW) / 2;
                        const y = 112 - h;
                        return (
                          <g key={d.label}>
                            <rect
                              data-analytics-bar
                              data-height={h}
                              data-y={y}
                              x={x}
                              y={y}
                              width={barW}
                              height={h}
                              rx="4"
                              fill="#2a5ce6"
                            />
                            <text
                              x={x + barW / 2}
                              y={130}
                              textAnchor="middle"
                              fontSize="9"
                              fill="#7981a1"
                            >
                              {d.label}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>

                  <div
                    data-feature-pop
                    className="rounded-[1.75rem] bg-white border border-ink-100 shadow-elevated p-5 space-y-3"
                  >
                    <p className="text-xs font-semibold text-ink-500">
                      {t("Top links")}
                    </p>
                    {ANALYTICS_LINKS.map((link) => (
                      <div key={link.label} className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-full bg-ink-50 flex items-center justify-center shrink-0">
                          <LinkIcon value={link.icon} color={link.color} className="text-xs w-3.5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-ink-800 truncate">
                              {t(link.label)}
                            </span>
                            <span className="text-xs text-ink-400 shrink-0">
                              {link.clicks.toLocaleString()}
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-brand-500"
                              style={{ width: `${link.pct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Feature: QR code generator */}
            <section className="max-w-8xl mx-auto px-6 md:px-10 py-20 md:py-28 overflow-hidden">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <div data-feature="left">
                  <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
                    {t("QR codes")}
                  </p>
                  <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-ink-900">
                    {t("Bring offline traffic online.")}
                  </h2>
                  <p className="mt-5 text-base text-ink-500 max-w-md leading-relaxed">
                    {t(
                      "Turn your Linkakam profile or any custom URL into a QR code you can print, share and download as a PNG or SVG. Customize the foreground and background colors, and add the Linkakam logo right in the center."
                    )}
                  </p>

                  <div className="mt-7 flex flex-wrap items-center gap-6">
                    <div>
                      <p className="text-xs font-medium text-ink-500 mb-2">{t("Foreground")}</p>
                      <div className="flex flex-wrap gap-2 max-w-[180px]">
                        {QR_FG_SWATCHES.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setQrFg(c)}
                            aria-label={c}
                            aria-pressed={qrFg === c}
                            className={`w-8 h-8 rounded-full border-2 transition-transform ${
                              qrFg === c ? "border-ink-900 scale-110" : "border-transparent"
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-ink-500 mb-2">{t("Background")}</p>
                      <div className="flex flex-wrap gap-2 max-w-[140px]">
                        {QR_BG_SWATCHES.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setQrBg(c)}
                            aria-label={c}
                            aria-pressed={qrBg === c}
                            className={`w-8 h-8 rounded-full border-2 transition-transform ${
                              qrBg === c ? "border-ink-900 scale-110" : "border-ink-200"
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <Button to="/en/qrgenrater" variant="outline" size="md">
                      {t("Create your QR code")}
                    </Button>
                  </div>
                </div>

                <div data-feature="right" className="relative flex justify-center">
                  <div
                    aria-hidden="true"
                    className="absolute -inset-8 rounded-full bg-accent-50 blur-3xl -z-10"
                  />
                  <div
                    className="relative rounded-[2rem] border border-ink-100 shadow-elevated p-6"
                    style={{ background: qrBg, transition: "background-color 0.3s ease-in-out" }}
                  >
                    <div className="relative w-52 h-52">
                      <QRCode
                        value="https://linkakam.com/yourname"
                        size={208}
                        level="H"
                        fgColor={qrFg}
                        bgColor={qrBg}
                        className="w-full h-full transition-colors duration-300"
                      />
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div
                          className="w-11 h-11 rounded-lg flex items-center justify-center transition-colors duration-300"
                          style={{ background: qrBg }}
                        >
                          <Logo className="w-7" title="" />
                        </div>
                      </div>
                    </div>
                    <p className="mt-4 text-center text-xs font-medium text-ink-400">
                      linkakam.com/yourname
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </>
      ) : (
        <section className="bg-ink-50 min-h-[70vh]">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <button
              type="button"
              onClick={clearSearch}
              className="inline-flex items-center gap-2 min-h-[44px] -ml-2 px-2 text-sm font-semibold text-ink-600 hover:text-brand-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              {t("Back to home")}
            </button>

            <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <h1 className="font-display font-extrabold text-3xl md:text-4xl text-ink-900">
                  {t("Search results")}
                </h1>
                <p className="mt-2 text-sm text-ink-500" aria-live="polite">
                  {loading
                    ? t("Searching...")
                    : `${profiles.length} ${
                        profiles.length === 1 ? t("profile") : t("profiles")
                      } ${t("for")} “${activeQuery}”`}
                </p>
              </div>

              <div className="w-full md:max-w-sm">
                <label htmlFor="results-search" className="sr-only">
                  {t("Search For Profile")}
                </label>
                <div className="flex items-center gap-2 rounded-full border border-ink-200 bg-white pl-4 pr-1.5 h-12 focus-within:border-brand-500 transition-colors">
                  <Search className="w-5 h-5 text-ink-400 shrink-0" aria-hidden="true" />
                  <input
                    id="results-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && load()}
                    placeholder={t("Search For Profile")}
                    type="text"
                    className="w-full text-sm text-ink-800 placeholder-ink-400 bg-transparent focus:outline-none"
                  />
                  <button
                    onClick={load}
                    className="h-9 px-4 rounded-full text-sm font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 transition-colors shrink-0"
                  >
                    {t("Search")}
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" aria-hidden="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <li key={i} className="h-24 rounded-2xl bg-white border border-ink-100 animate-pulse" />
                ))}
              </ul>
            ) : profiles.length === 0 ? (
              <div className="mt-16 text-center max-w-sm mx-auto">
                <div className="w-16 h-16 mx-auto rounded-full bg-white border border-ink-100 flex items-center justify-center">
                  <Logo className="w-9 text-ink-300" title="" />
                </div>
                <p className="mt-5 font-display font-bold text-xl text-ink-900">
                  {t("No profiles found")}
                </p>
                <p className="mt-2 text-sm text-ink-500">
                  {t("Check the spelling or try a different username.")}
                </p>
              </div>
            ) : (
              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {profiles.map((element) => (
                  <li key={element.username}>
                    <Link
                      to={`/${element.username}`}
                      className="group flex items-center gap-4 p-4 rounded-2xl bg-white border border-ink-100 hover:border-brand-200 hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      <img
                        alt=""
                        src={
                          element?.image
                            ? `${API_ORIGIN}/profile/${element.image}`
                            : `${process.env.PUBLIC_URL}/images/df.png`
                        }
                        className="w-14 h-14 rounded-full object-cover bg-ink-100 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-display font-bold text-ink-900 truncate">
                          @{element.username}
                        </p>
                        <p className="mt-0.5 text-xs text-ink-400 truncate">
                          linkakam.com/{element.username}
                        </p>
                      </div>
                      <ArrowRight
                        className="w-4 h-4 shrink-0 text-ink-300 group-hover:text-brand-600 group-hover:translate-x-0.5 transition-all"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
    </>
  );
};

export default Home;
